import { useEffect, useState } from "react";

const STORAGE_KEY = "omnix.quotationTypeView";
const VALID = new Set(["all", "online", "offline"]);

// Subscribers across pages so toggling on one analytics tab updates others mounted in parallel.
const subscribers = new Set();
const notify = (v) => subscribers.forEach((fn) => { try { fn(v); } catch { /* noop */ } });

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID.has(v) ? v : "all";
  } catch {
    return "all";
  }
};

/**
 * Shared, persisted "Total | Online | Offline" view selector.
 * - Defaults to "all" (Total = Online + Offline)
 * - Persists to localStorage so the selection survives navigation/refresh
 * - All instances stay in sync via an in-memory pub/sub
 */
export function useQuotationTypeView() {
  const [view, setView] = useState(readStored);

  useEffect(() => {
    const onChange = (v) => setView(v);
    subscribers.add(onChange);
    // Also react to localStorage changes from other browser tabs.
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setView(readStored()); };
    window.addEventListener("storage", onStorage);
    return () => {
      subscribers.delete(onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const update = (v) => {
    if (!VALID.has(v)) return;
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* noop */ }
    notify(v);
    setView(v);
  };

  return [view, update];
}

// Predicate helper used by analytics filter pipelines.
// view: "all" | "online" | "offline"
export const matchesQuotationType = (q, view) => {
  if (view === "all" || !view) return true;
  const t = (q?.quotation_type || "offline").toLowerCase();
  return t === view;
};
